/**
 * JSON-LD structured data for SEO.
 *
 * Emits schema.org structured data through react-helmet-async so Google,
 * Bing and other search engines can build a knowledge panel for the
 * company and surface rich results for services, breadcrumbs and FAQs.
 *
 * Two shapes are supported:
 *   <JsonLd data={singleSchema} />
 *   <JsonLd data={[schemaA, schemaB, ...]} />
 *
 * Helper factories live below — they keep page components clean and the
 * source of truth for company facts in one file.
 */
import { Helmet } from "react-helmet-async";

const PROD_ORIGIN = "https://nojoenterprise.com";

function abs(url) {
  if (!url) return url;
  if (/^https?:/i.test(url)) return url;
  return `${PROD_ORIGIN}${url.startsWith("/") ? "" : "/"}${url}`;
}

export default function JsonLd({ data }) {
  if (!data) return null;
  const payload = Array.isArray(data) ? data : [data];
  return (
    <Helmet>
      {payload.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Canonical company facts (single source of truth for structured data).
// Keep in sync with /pages/CorporateProfilePage.jsx and /pages/ContactPage.jsx.
// ─────────────────────────────────────────────────────────────────────────────

export const COMPANY = {
  legalName: "NoJo Enterprise Co., Ltd.",
  legalNameJa: "株式会社NoJo Enterprise",
  brand: "NoJo Enterprise",
  url: PROD_ORIGIN,
  logo: abs("/social/nojo-logo-source.png"),
  founded: "2013",
  founder: "Adam Jones",
  phone: "+81-46-874-7156",
  email: "support@nojoenterprise.com",
  street: "2-2-1 Honcho",
  city: "Yokosuka",
  region: "Kanagawa",
  postal: "238-0041",
  country: "JP",
  // 35.2828° N, 139.6708° E — Yokosuka, Kanagawa
  latitude: 35.2828,
  longitude: 139.6708,
  sameAs: [
    "https://japanetccard.com",
    "https://japantollreceipts.jp",
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Schema factories
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Organization + LocalBusiness combined schema — emit on the home page.
 * Includes physical address, contact points (EN + JA), founders and
 * operated services.
 */
export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": ["Organization", "LocalBusiness"],
    "@id": `${COMPANY.url}/#organization`,
    name: COMPANY.legalName,
    legalName: COMPANY.legalName,
    alternateName: [COMPANY.legalNameJa, COMPANY.brand],
    url: COMPANY.url,
    logo: COMPANY.logo,
    image: COMPANY.logo,
    foundingDate: COMPANY.founded,
    founder: {
      "@type": "Person",
      name: COMPANY.founder,
      jobTitle: "Founder & Representative Director",
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: COMPANY.street,
      addressLocality: COMPANY.city,
      addressRegion: COMPANY.region,
      postalCode: COMPANY.postal,
      addressCountry: COMPANY.country,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: COMPANY.latitude,
      longitude: COMPANY.longitude,
    },
    telephone: COMPANY.phone,
    email: COMPANY.email,
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: COMPANY.phone,
        contactType: "customer support",
        availableLanguage: ["English", "Japanese"],
        email: COMPANY.email,
        areaServed: "JP",
      },
    ],
    sameAs: COMPANY.sameAs,
    knowsAbout: [
      "ETC card support for foreign residents in Japan",
      "NEXCO toll record automation",
      "MEISAI extraction and reporting",
      "Currency exchange in Japan",
      "Bilingual operations (English / Japanese)",
    ],
  };
}

/**
 * WebSite schema — emit on the home page. Enables Google's Sitelinks search
 * box (though our site doesn't yet have on-site search, declaring intent is
 * harmless and improves entity disambiguation).
 */
export function websiteSchema({ lang = "en" } = {}) {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${COMPANY.url}/#website`,
    url: COMPANY.url,
    name: COMPANY.brand,
    inLanguage: lang === "ja" ? "ja-JP" : "en-US",
    publisher: { "@id": `${COMPANY.url}/#organization` },
  };
}

/**
 * BreadcrumbList — emit on every non-home page. Powers the breadcrumb
 * line that Google displays under the page title in SERPs.
 *
 * @param {{name:string,url:string}[]} crumbs — ordered list ending in the
 *   current page.
 */
export function breadcrumbSchema(crumbs) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: abs(c.url),
    })),
  };
}

/**
 * Service schema — emit on each service detail page so Google understands
 * what each domain (JapanETCcard, JapanTollReceipts, JapanChangeMoney)
 * actually delivers, and to which audience.
 */
export function serviceSchema({
  name,
  description,
  url,
  serviceType,
  areaServed = "JP",
  brand = COMPANY.brand,
  inLanguage,
  providerLogo,
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    serviceType,
    url: abs(url),
    inLanguage: inLanguage === "ja" ? "ja-JP" : "en-US",
    provider: {
      "@type": "Organization",
      "@id": `${COMPANY.url}/#organization`,
      name: COMPANY.legalName,
      logo: providerLogo || COMPANY.logo,
    },
    brand: { "@type": "Brand", name: brand },
    areaServed: { "@type": "Country", name: areaServed === "JP" ? "Japan" : areaServed },
  };
}

/**
 * Person schema — used on Leadership and Founder's Message pages so Google
 * can build a verified attribution between the founder and the company.
 */
export function personSchema({ name, jobTitle, image, sameAs = [] }) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name,
    jobTitle,
    image: image ? abs(image) : undefined,
    worksFor: { "@id": `${COMPANY.url}/#organization` },
    sameAs,
  };
}

/**
 * FAQPage schema — wrap an array of {question, answer} pairs. Eligible
 * for rich-result FAQ accordions in Google search results.
 */
export function faqSchema(qaPairs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: qaPairs.map((qa) => ({
      "@type": "Question",
      name: qa.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: qa.answer,
      },
    })),
  };
}
